import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSubscriptionActive, useWorkspaceId } from '@/contexts/WorkspaceContext';

// Hard gate for action pages: when the workspace has no active subscription the page
// is rendered blurred and non-interactive behind a blocking overlay (no dismiss).
// The backend also enforces requireActiveSubscription.
export default function SubscriptionGate({ children }: { children: ReactNode }) {
  const active = useSubscriptionActive();
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();

  if (active) return <>{children}</>;

  return (
    <>
      <div className="blur-sm pointer-events-none select-none" aria-hidden="true">
        {children}
      </div>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4">
        <Card className="max-w-md w-full p-8 text-center shadow-2xl border-border/60">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <h2 className="text-xl font-bold mb-2">Subscription required</h2>
          <p className="text-muted-foreground mb-6">
            You need an active subscription to use this feature. Choose a plan to unlock your workspace.
          </p>
          <div className="space-y-2">
            <Button className="w-full" onClick={() => navigate(`/w/${workspaceId}/billing`)}>View plans</Button>
            <Button variant="ghost" className="w-full" onClick={() => navigate(`/w/${workspaceId}/dashboard`)}>
              Back to dashboard
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}
